import React from 'react';
import './PilhaDeAvatares.css';

/**
 * A pilha de iniciais — quem está na meta, numa linha só.
 *
 * ── `aria-hidden`, e isso não é preguiça de acessibilidade ───────────────
 *
 * A pilha é sempre acompanhada da lista de nomes logo abaixo (colapsada, a
 * linha "você pôs R$ X"; aberta, a lista inteira). Para quem usa leitor de
 * tela, ler "C, Ca, L, A" antes dos nomes completos é ruído que atrasa a
 * informação de verdade.
 *
 * É a mesma decisão do ícone da marca em `App.tsx`: o elemento decorativo cala
 * quando o texto ao lado já diz a mesma coisa. Se um dia a pilha aparecer
 * SOZINHA, sem a lista, esta decisão precisa ser revista — e é por isso que
 * está escrita.
 *
 * ── Iniciais, e não foto ─────────────────────────────────────────────────
 *
 * Foto exige upload, storage e moderação. A inicial resolve o que a pilha
 * precisa resolver: dizer, de relance, quantas pessoas estão juntas nisso.
 *
 * ── A cor sai do NOME, não de uma paleta rotativa ────────────────────────
 *
 * Com paleta rotativa por índice, a mesma pessoa muda de cor quando alguém
 * entra ou sai da meta — e a lista é ordenada por valor, então ela muda de cor
 * toda vez que alguém aporta. A cor derivada do nome é estável enquanto o nome
 * for, que é o que faz a pilha ser reconhecível de relance.
 */

/**
 * Os tons da pilha. Todos escolhidos com texto branco por cima em mente: o
 * mais claro daqui, medido contra `#ffffff`, fica acima de 4,5:1.
 */
const TONS = [
  '#6d4fc2', '#2563eb', '#0891b2', '#059669',
  '#ca8a04', '#dc2626', '#db2777', '#7c3aed',
];

/**
 * Um número estável a partir do texto.
 *
 * Percorre UNIDADES de código, não pontos de código — e está escrito porque a
 * distinção é tentadora aqui e não importa: o valor só precisa ser estável
 * para o mesmo nome e bem distribuído. Um par substituto contado como duas
 * unidades não muda nenhuma das duas coisas.
 *
 * (O `target` deste projeto é `es5`, onde um `for...of` sobre string vira um
 * laço por índice de qualquer jeito. Escrever `codePointAt` daria a aparência
 * de uma garantia que o compilador não entrega.)
 */
export const tomDoNome = (nome: string): string => {
  let n = 0;
  for (let i = 0; i < nome.length; i += 1) n = (n * 31 + nome.charCodeAt(i)) % 100000;
  return TONS[n % TONS.length];
};

/**
 * A inicial mostrada.
 *
 * `Array.from` e não `nome[0]`: quando o nome começa com um caractere fora do
 * plano básico — um emoji, que num campo de nome acontece — `[0]` devolve
 * METADE de um par substituto, e o avatar renderiza o losango de erro.
 *
 * O que ele NÃO conserta, e por isso não está prometido aqui: acento
 * COMBINANTE. Em "Â" escrito como `A` + U+0302, `Array.from` separa os dois e
 * a inicial sai pelada — resolver isso exigiria `Intl.Segmenter`, e a troca
 * não se paga por uma letra de avatar. "Â" pré-composto (U+00C2), que é o que
 * praticamente todo teclado produz, é um ponto de código só e funciona.
 */
export const inicialDe = (nome: string): string => {
  const limpo = nome.trim();
  if (!limpo) return '?';
  return Array.from(limpo)[0].toLocaleUpperCase('pt-BR');
};

export interface PessoaNaPilha { userId: string | null; nome: string }

export const PilhaDeAvatares: React.FC<{
  pessoas: PessoaNaPilha[];
  /** Acima disso, o excedente vira "+N". Padrão 5. */
  teto?: number;
}> = ({ pessoas, teto = 5 }) => {
  if (pessoas.length === 0) return null;

  const mostradas = pessoas.slice(0, teto);
  const sobra = pessoas.length - mostradas.length;

  return (
    <div className="pilha" aria-hidden="true">
      {mostradas.map((p, i) => (
        <span
          key={p.userId ?? `${p.nome}-${i}`}
          className="pilha-avatar"
          style={{ background: tomDoNome(p.nome) }}
        >
          {inicialDe(p.nome)}
        </span>
      ))}
      {sobra > 0 && <span className="pilha-avatar pilha-sobra">+{sobra}</span>}
    </div>
  );
};

export default PilhaDeAvatares;
